import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

const ALLOWED_LANGS = ["fr", "fr_en", "fr_es"];
const ALLOWED_TYPES = ["eco", "berline", "tesla", "van", "moto"];

function buildInitialForm(ride) {
  return {
    info_block: ride.info_block || "",
    pickup_date: ride.pickup_date || "",
    pickup_time: ride.pickup_time || "",
    route_label: ride.route_label || "",
    // Legacy rides may carry any ride_type — keep the stored value even if
    // it's outside our current select list (we never silently coerce on edit).
    ride_type: ride.ride_type || "eco",
    driver_fare: String(ride.driver_fare || ""),
    // Phase 30 — finance fields. Show stored values if present, blank means "mirror driver_fare".
    client_price: ride.client_price != null ? String(ride.client_price) : "",
    driver_amount: ride.driver_amount != null ? String(ride.driver_amount) : "",
    is_pab: !!ride.is_pab,
    passenger_count: ride.passenger_count != null ? String(ride.passenger_count) : "",
    luggage_count: ride.luggage_count != null ? String(ride.luggage_count) : "",
    notes: ride.notes || "",
    // Phase 34 — document intent flags (boolean, default false on legacy).
    invoice_requested: !!ride.invoice_requested,
    voucher_requested: !!ride.voucher_requested,
    // Phase 35 — document language; legacy/unknown collapses to "fr".
    document_language: ALLOWED_LANGS.includes(ride.document_language) ? ride.document_language : "fr",
  };
}

// Inner form — mounted with a `key={ride.id}` from the outer component so the
// form state is re-initialised on ride switch without needing setState-in-effect.
function EditRideForm({ ride, onClose, onDone }) {
  const [form, setForm] = useState(() => buildInitialForm(ride));
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: typeof e === "string" ? e : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/rides/${ride.id}`, {
        ...form, driver_fare: parseFloat(form.driver_fare),
        client_price: form.client_price !== "" ? parseFloat(form.client_price) : null,
        driver_amount: form.driver_amount !== "" ? parseFloat(form.driver_amount) : null,
        is_pab: !!form.is_pab,
        passenger_count: form.passenger_count ? parseInt(form.passenger_count) : null,
        luggage_count: form.luggage_count ? parseInt(form.luggage_count) : null,
        // Phase 34 — document intent (always send as boolean)
        invoice_requested: !!form.invoice_requested,
        voucher_requested: !!form.voucher_requested,
        // Phase 35 — document language (server whitelists, fallback "fr")
        document_language: form.document_language || "fr",
      });
      toast.success("Ride updated");
      onDone?.(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
    finally { setLoading(false); }
  };

  // If the stored ride_type isn't in our current dropdown, render it as an
  // extra item so the Select doesn't silently reset on legacy rides.
  const typeOptions = ALLOWED_TYPES.includes(form.ride_type)
    ? ALLOWED_TYPES
    : [...ALLOWED_TYPES, form.ride_type];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Ride Information</Label><Textarea data-testid="edit-info-block" value={form.info_block} onChange={set("info_block")} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 min-h-[100px] text-sm" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Date</Label><Input type="date" value={form.pickup_date} onChange={set("pickup_date")} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" /></div>
        <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Time</Label><Input type="time" value={form.pickup_time} onChange={set("pickup_time")} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" /></div>
      </div>
      <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Route</Label><Input value={form.route_label} onChange={set("route_label")} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10 font-medium" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Type</Label>
          <Select value={form.ride_type} onValueChange={(v) => setForm(p => ({...p, ride_type: v}))}>
            <SelectTrigger data-testid="edit-ride-type" className="bg-[#1A1A1A] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-zinc-700">
              {typeOptions.map((v) => (
                <SelectItem key={v} value={v} className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-zinc-400 text-xs uppercase tracking-wider" title="Tarif de base. Sert de valeur par défaut pour le Prix client et le Montant chauffeur.">Tarif base (EUR)</Label>
          <Input
            data-testid="edit-driver-fare"
            type="number" step="0.01"
            value={form.driver_fare}
            onChange={set("driver_fare")}
            title="Tarif de base. Sert de valeur par défaut pour le Prix client et le Montant chauffeur."
            className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10"
          />
          <p className="text-[10px] text-zinc-600 leading-tight">Valeur par défaut pour Prix client et Montant chauffeur.</p>
        </div>
      </div>
      {/* Phase 30 — financial structure. Optional fields. Leave blank to mirror Fare. */}
      <div className="border border-zinc-800 rounded-lg p-3 space-y-3 bg-[#0e0e0e]">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">Détail financier</Label>
          {(() => {
            const cp = parseFloat(form.client_price || form.driver_fare || 0) || 0;
            const da = parseFloat(form.driver_amount || form.driver_fare || 0) || 0;
            const com = Math.max(0, cp - da);
            return <span data-testid="edit-finance-commission-preview" className="text-[11px] text-amber-400">Commission auto · <strong>{com.toFixed(2)} EUR</strong></span>;
          })()}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-zinc-500 text-[11px] uppercase tracking-wider">Prix client (EUR)</Label><Input data-testid="edit-client-price" type="number" step="0.01" value={form.client_price} onChange={set("client_price")} placeholder={form.driver_fare || "auto"} className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" /></div>
          <div className="space-y-1.5"><Label className="text-zinc-500 text-[11px] uppercase tracking-wider">Montant chauffeur (EUR)</Label><Input data-testid="edit-driver-amount" type="number" step="0.01" value={form.driver_amount} onChange={set("driver_amount")} placeholder={form.driver_fare || "auto"} className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
          <input data-testid="edit-is-pab" type="checkbox" checked={!!form.is_pab} onChange={(e) => setForm(p => ({ ...p, is_pab: e.target.checked }))} className="h-4 w-4 accent-amber-500" />
          <span>PAB <span className="text-zinc-500 text-xs">— le chauffeur voit le prix client</span></span>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Passengers</Label><Input type="number" min="0" value={form.passenger_count} onChange={set("passenger_count")} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" /></div>
        <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Luggage</Label><Input type="number" min="0" value={form.luggage_count} onChange={set("luggage_count")} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" /></div>
      </div>
      <div className="space-y-1.5"><Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label><Textarea value={form.notes} onChange={set("notes")} className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 min-h-[60px] text-sm" /></div>

      {/* Phase 34 / 35 — Documents à préparer + langue. */}
      <div className="border border-zinc-800 rounded-lg p-3 space-y-2 bg-[#0e0e0e]">
        <Label className="text-zinc-400 text-xs uppercase tracking-wider">Documents à préparer</Label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            data-testid="edit-invoice-requested"
            type="checkbox"
            checked={!!form.invoice_requested}
            onChange={(e) => setForm(p => ({ ...p, invoice_requested: e.target.checked }))}
            className="w-4 h-4 rounded border-zinc-600 text-amber-500 focus:ring-amber-500 bg-[#0a0a0a]"
          />
          <span className="text-sm text-white">Facture client</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            data-testid="edit-voucher-requested"
            type="checkbox"
            checked={!!form.voucher_requested}
            onChange={(e) => setForm(p => ({ ...p, voucher_requested: e.target.checked }))}
            className="w-4 h-4 rounded border-zinc-600 text-amber-500 focus:ring-amber-500 bg-[#0a0a0a]"
          />
          <span className="text-sm text-white">Voucher / confirmation de transfert</span>
        </label>
        <div className="space-y-1.5 pt-2 border-t border-zinc-800">
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Langue des documents</Label>
          <Select value={form.document_language} onValueChange={(v) => setForm(p => ({...p, document_language: v}))}>
            <SelectTrigger data-testid="edit-document-language" className="bg-[#0a0a0a] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-zinc-700">
              <SelectItem value="fr" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Français uniquement</SelectItem>
              <SelectItem value="fr_en" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Français + Anglais</SelectItem>
              <SelectItem value="fr_es" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Français + Espagnol</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-zinc-600 leading-tight">Le français est toujours présent. La 2ᵉ langue ne sera utilisée que par la facture et le voucher.</p>
        </div>
      </div>

      <Button data-testid="edit-ride-submit" type="submit" disabled={loading} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 active:scale-[0.98] h-11">{loading ? "Saving..." : "Save Changes"}</Button>
    </form>
  );
}

export default function EditRideModal({ open, onClose, ride, onDone }) {
  if (!ride) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Ride</DialogTitle>
          <DialogDescription className="text-zinc-500">{ride.route_label}</DialogDescription>
        </DialogHeader>
        {/* key on the inner form guarantees fresh state when switching rides
            without needing setState-in-effect. */}
        <EditRideForm key={ride.id} ride={ride} onClose={onClose} onDone={onDone} />
      </DialogContent>
    </Dialog>
  );
}
